// WebSocket Service - Manages real-time connections using Laravel Echo
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { getBackendHost, resolveBackendUrl, useLocalProxy } from '@/config/apiConfig';

// Make Pusher available globally for Laravel Echo
window.Pusher = Pusher;

let echoInstance = null;

export const initializeWebSocket = (token) => {
  if (echoInstance) {
    return echoInstance;
  }

  const authEndpoint = resolveBackendUrl('/broadcasting/auth');
  const wsHost = process.env.REACT_APP_WS_HOST || getBackendHost();
  const wsScheme = (process.env.REACT_APP_WS_SCHEME || 'wss').toLowerCase();
  const wsPort = Number(process.env.REACT_APP_WS_PORT || (wsScheme === 'wss' ? 443 : 80));
  const appKey = process.env.REACT_APP_PUSHER_KEY;
  if (!appKey) {
    throw new Error('REACT_APP_PUSHER_KEY is required for realtime features');
  }
  const forceTLS = wsScheme === 'wss';

  echoInstance = new Echo({
    broadcaster: 'pusher',
    key: appKey,
    cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'local',
    wsHost: wsHost,
    wsPort: wsPort,
    wssPort: wsPort,
    forceTLS,
    encrypted: forceTLS,
    disableStats: true,
    enabledTransports: forceTLS ? ['wss'] : ['ws'],
    authEndpoint,
    auth: {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  });

  return echoInstance;
};

export const getEchoInstance = () => echoInstance;

export const isWebSocketProxyMode = () => useLocalProxy;

export const disconnectWebSocket = () => {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
};

// Subscribe to a conversation channel for real-time messages
export const subscribeToConversation = (conversationId, callbacks) => {
  if (!echoInstance) {
    console.warn('WebSocket not initialized');
    return null;
  }

  const channel = echoInstance.private(`conversation.${conversationId}`);

  if (callbacks.onMessage) {
    channel.listen('.message.new', callbacks.onMessage);
  }

  if (callbacks.onMessageRead) {
    channel.listen('.message.read', callbacks.onMessageRead);
  }

  return channel;
};

// Unsubscribe from a conversation channel
export const unsubscribeFromConversation = (conversationId) => {
  if (echoInstance) {
    echoInstance.leave(`conversation.${conversationId}`);
  }
};

// Subscribe to user channel for calls and private notifications
export const subscribeToUserChannel = (userId, callbacks) => {
  if (!echoInstance) {
    console.warn('WebSocket not initialized');
    return null;
  }

  const channel = echoInstance.private(`user.${userId}`);

  if (callbacks.onIncomingCall) {
    channel.listen('.call.incoming', callbacks.onIncomingCall);
  }

  if (callbacks.onCallSignal) {
    channel.listen('.call.signal', callbacks.onCallSignal);
  }

  if (callbacks.onNotification) {
    channel.listen('.notification.new', callbacks.onNotification);
  }

  if (callbacks.onAppointmentChanged) {
    channel.listen('.appointment.changed', callbacks.onAppointmentChanged);
  }

  return channel;
};

export const unsubscribeFromUserChannel = (userId) => {
  if (echoInstance) {
    echoInstance.leave(`user.${userId}`);
  }
};

export default {
  initializeWebSocket,
  getEchoInstance,
  disconnectWebSocket,
  subscribeToConversation,
  unsubscribeFromConversation,
  subscribeToUserChannel,
  unsubscribeFromUserChannel,
};
